/**
 * Admin — Demo Pages.
 *
 * Manages the password-gated PUBLIC demo pages the BFF serves outside the
 * MSAL login (vera.oneveracity.com/<slug>). List / activate / deactivate /
 * delete existing pages, and upload a new standalone HTML file with a slug
 * and access word for the next opportunity.
 *
 * Everything here is effectively public — the access word is a shared
 * velvet rope, not auth. The page screams this at the admin via the warning
 * banner; keep it that way.
 */

import { useMemo, useRef, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle2,
  Switch,
  Title2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUploadRegular,
  CopyRegular,
  DeleteRegular,
  GlobeRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '@shared/api';
import { veracityColors } from '@shared/ui';

// =============================================================================
// API
// =============================================================================

interface DemoPageMeta {
  slug: string;
  title: string;
  accessWord: string;
  active: boolean;
  /** Date-only ISO; the page serves through this date and auto-deactivates after. */
  expiresAt: string;
  size: number;
  updatedAt: string;
  createdBy?: string;
}

/** Default page lifetime: 4 months out (mirrors the BFF default). */
function defaultExpiry(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 4);
  return d.toISOString().slice(0, 10);
}

function isExpired(page: DemoPageMeta): boolean {
  return !!page.expiresAt && new Date().toISOString().slice(0, 10) > page.expiresAt;
}

async function fetchPages(): Promise<DemoPageMeta[]> {
  const res = await bffFetch('/api/demo-pages');
  if (!res.ok) throw new Error(`Failed to load demo pages (${res.status})`);
  const data = (await res.json()) as { pages: DemoPageMeta[] };
  return data.pages;
}

async function expectOk(res: Response): Promise<void> {
  if (res.ok) return;
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // non-JSON error body — keep the status message
  }
  throw new Error(message);
}

const QUERY_KEY = ['demoPages'];

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  titleIcon: { fontSize: '28px', color: veracityColors.veracityOrange },
  subtitle: { color: veracityColors.duskBlue, marginBottom: tokens.spacingVerticalL },
  warning: { marginBottom: tokens.spacingVerticalL },
  table: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#FFFFFF',
    marginBottom: tokens.spacingVerticalXL,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1.3fr 0.9fr 0.9fr 150px 70px 80px 60px',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${veracityColors.firstLight}`,
    ':last-child': { borderBottom: 'none' },
  },
  headRow: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.duskBlue,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pageTitle: { fontWeight: tokens.fontWeightSemibold, color: veracityColors.midnightCharcoal },
  mono: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.midnightCharcoal,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  muted: { color: veracityColors.duskBlue, fontSize: tokens.fontSizeBase200 },
  inactive: { opacity: 0.55 },
  uploadCard: { padding: tokens.spacingVerticalL, backgroundColor: '#FFFFFF' },
  uploadGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
});

// =============================================================================
// Component
// =============================================================================

function fmtSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function DemoPagesPage() {
  const styles = useStyles();
  const qc = useQueryClient();

  const pagesQuery = useQuery({ queryKey: QUERY_KEY, queryFn: fetchPages });

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const patchMutation = useMutation({
    mutationFn: async (args: { slug: string; patch: Record<string, unknown> }) => {
      const res = await bffFetch(`/api/demo-pages/${args.slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args.patch),
      });
      await expectOk(res);
    },
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (slug: string) => {
      const res = await bffFetch(`/api/demo-pages/${slug}`, { method: 'DELETE' });
      await expectOk(res);
    },
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  // ── Upload form state ──
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [accessWord, setAccessWord] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => defaultExpiry());
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick an HTML file first.');
      const html = await file.text();
      const res = await bffFetch('/api/demo-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slug.trim(),
          title: title.trim(),
          accessWord: accessWord.trim(),
          expiresAt,
          html,
        }),
      });
      await expectOk(res);
    },
    onSuccess: () => {
      invalidate();
      setNotice(`Published /${slug.trim()} — try it in a private window before sharing.`);
      setSlug('');
      setTitle('');
      setAccessWord('');
      setExpiresAt(defaultExpiry());
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  const canUpload =
    !!file &&
    slug.trim().length >= 2 &&
    title.trim().length > 0 &&
    accessWord.trim().length >= 4 &&
    /^\d{4}-\d{2}-\d{2}$/.test(expiresAt);

  // Dev runs split-origin: Vite (5176) proxies only /auth,/geocode,/api to the
  // BFF (3004), so public demo slugs exist ONLY on the BFF origin. Point the
  // copy/open buttons there in dev; in prod both share one origin.
  const origin = useMemo(
    () => (import.meta.env.DEV ? 'http://localhost:3004' : window.location.origin),
    [],
  );

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  const handleDelete = (page: DemoPageMeta) => {
    // window.confirm keeps this dependency-free; the action is rare and admin-only.
    if (window.confirm(`Delete /${page.slug} (“${page.title}”)? The URL stops working immediately and the file is removed.`)) {
      setError(null);
      deleteMutation.mutate(page.slug);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <GlobeRegular className={styles.titleIcon} />
        <Title2>Demo Pages</Title2>
      </div>
      <Body1 className={styles.subtitle}>
        Standalone HTML pages served at a short public URL behind a one-word access gate — for
        showing sales material on a prospect&apos;s machine without signing into Vera.
      </Body1>

      <MessageBar intent="warning" layout="multiline" className={styles.warning}>
        <MessageBarBody>
          <MessageBarTitle>These pages are effectively PUBLIC. No PHI. Nothing confidential.</MessageBarTitle>
          The access word is a shared, unauditable speed bump — it can be guessed, shared, or
          shoulder-surfed, and anyone who has it sees the page. Never upload member or claims data,
          PHI of any kind, pricing, contracts, or anything you wouldn&apos;t hand a stranger at a
          conference. Demo and marketing material only. Deactivate pages when the opportunity closes.
        </MessageBarBody>
      </MessageBar>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {notice && !error && (
        <MessageBar intent="success" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      {pagesQuery.isLoading ? (
        <Spinner label="Loading demo pages…" />
      ) : (
        <div className={styles.table}>
          <div className={`${styles.row} ${styles.headRow}`}>
            <span>Page</span>
            <span>URL</span>
            <span>Access word</span>
            <span>Expires</span>
            <span>Size</span>
            <span>Active</span>
            <span />
          </div>
          {(pagesQuery.data ?? []).length === 0 && (
            <div style={{ padding: tokens.spacingVerticalL, textAlign: 'center' }}>
              <Caption1 style={{ color: veracityColors.duskBlue }}>No demo pages yet.</Caption1>
            </div>
          )}
          {(pagesQuery.data ?? []).map((page) => (
            <div key={page.slug} className={page.active ? styles.row : `${styles.row} ${styles.inactive}`}>
              <div>
                <div className={styles.pageTitle}>{page.title}</div>
                <Caption1 className={styles.muted}>
                  updated {page.updatedAt.slice(0, 10)}
                  {page.createdBy && page.createdBy !== 'seed' ? ` · by ${page.createdBy}` : ''}
                </Caption1>
              </div>
              <span className={styles.mono}>
                /{page.slug}
                <Tooltip content="Copy full URL" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<CopyRegular />}
                    onClick={() => copy(`${origin}/${page.slug}`)}
                  />
                </Tooltip>
                <Tooltip content="Open page" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<OpenRegular />}
                    onClick={() => window.open(`${origin}/${page.slug}`, '_blank', 'noopener')}
                  />
                </Tooltip>
              </span>
              <span className={styles.mono}>
                {page.accessWord}
                <Tooltip content="Copy access word" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<CopyRegular />}
                    onClick={() => copy(page.accessWord)}
                  />
                </Tooltip>
              </span>
              <div>
                <Input
                  type="date"
                  size="small"
                  value={page.expiresAt ?? ''}
                  disabled={patchMutation.isPending}
                  onChange={(_, data) => {
                    if (!data.value) return;
                    setError(null);
                    patchMutation.mutate({ slug: page.slug, patch: { expiresAt: data.value } });
                  }}
                />
                {isExpired(page) && (
                  <Caption1 style={{ color: '#D6461D', display: 'block' }}>expired</Caption1>
                )}
              </div>
              <span className={styles.muted}>{fmtSize(page.size)}</span>
              <Switch
                checked={page.active}
                disabled={patchMutation.isPending}
                onChange={(_, data) => {
                  setError(null);
                  patchMutation.mutate({ slug: page.slug, patch: { active: data.checked } });
                }}
              />
              <Tooltip content="Delete page" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  disabled={deleteMutation.isPending}
                  onClick={() => handleDelete(page)}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      <Card className={styles.uploadCard}>
        <Subtitle2 style={{ display: 'block', marginBottom: tokens.spacingVerticalS }}>
          Publish a new page
        </Subtitle2>
        <Caption1 style={{ color: veracityColors.duskBlue, display: 'block', marginBottom: tokens.spacingVerticalM }}>
          Upload a self-contained HTML file (max 5MB). It goes live at {origin}/&lt;slug&gt; the
          moment you publish — re-read the warning above before you do.
        </Caption1>

        <div className={styles.uploadGrid}>
          <Field label="Slug (the URL path)" hint="lowercase letters, digits, hyphens">
            <Input
              value={slug}
              placeholder="austin-college"
              onChange={(_, d) => setSlug(d.value.toLowerCase())}
            />
          </Field>
          <Field label="Title" hint="shown on the access-word screen">
            <Input value={title} placeholder="Austin College Proximity" onChange={(_, d) => setTitle(d.value)} />
          </Field>
          <Field label="Access word" hint="at least 4 characters">
            <Input value={accessWord} placeholder="TexasCollege" onChange={(_, d) => setAccessWord(d.value)} />
          </Field>
          <Field label="Expires" hint="auto-deactivates after this date">
            <Input type="date" value={expiresAt} onChange={(_, d) => setExpiresAt(d.value)} />
          </Field>
        </div>

        <div className={styles.fileRow}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,text/html"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && <Caption1 className={styles.muted}>{fmtSize(file.size)}</Caption1>}
          <Button
            appearance="primary"
            icon={<ArrowUploadRegular />}
            disabled={!canUpload || uploadMutation.isPending}
            onClick={() => {
              setError(null);
              setNotice(null);
              uploadMutation.mutate();
            }}
          >
            {uploadMutation.isPending ? 'Publishing…' : 'Publish page'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default DemoPagesPage;
